import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from 'core/layout/Header';
import MinimalFooter from 'features/feature_home/components/MinimalFooter';
import JobsFilterSidebar from '../components/JobsFilterSidebar';
import JobsListSidebar from '../components/JobsListSidebar';
import {
    Box,
    Container,
    Grid,
    TextField,
    InputAdornment,
    Button,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchHero from 'core/components/SearchHero';
import { Search as SearchLucide, MapPin } from 'lucide-react';
import { useTranslation } from 'core/context/TranslationContext';


const EMPTY_FILTERS = {
    category: '', country: '', city: '', jobtype: '',
    schedule: '', experience_level: '', pay_min: '', pay_max: ''
};

function JobsPage() {
    const location = useLocation();
    const { _t } = useTranslation();

    // Read search params passed from the home page search bar
    const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const urlSearch = urlParams.get('search') || '';
    const urlLocation = urlParams.get('location') || '';

    const [searchInput, setSearchInput] = useState(urlSearch);
    const [locationInput, setLocationInput] = useState(urlLocation);
    const [isSearching, setIsSearching] = useState(false);

    const [quickFilters, setQuickFilters] = useState(() => {
        const q = {};
        if (urlSearch) q.search = urlSearch;
        if (urlLocation) q.location = urlLocation;
        return q;
    });

    const [sidebarFilters, setSidebarFilters] = useState(EMPTY_FILTERS);
    const [appliedSidebarFilters, setAppliedSidebarFilters] = useState({});

    // Keep search inputs in sync when URL changes
    useEffect(() => {
        setSearchInput(urlSearch);
        setLocationInput(urlLocation);
        const q = {};
        if (urlSearch) q.search = urlSearch;
        if (urlLocation) q.location = urlLocation;
        setQuickFilters(q);
    }, [urlSearch, urlLocation]);

    const sanitize = (obj) =>
        Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined));

    const mergedFilters = useMemo(
        () => sanitize({ ...appliedSidebarFilters, ...quickFilters }),
        [appliedSidebarFilters, quickFilters]
    );

    const handleQuickSearch = (e) => {
        e.preventDefault();
        const q = {};
        if (searchInput.trim()) q.search = searchInput.trim();
        if (locationInput.trim()) q.location = locationInput.trim();
        setQuickFilters(q);
    };

    const handleApplySidebar = (newFilters) => setAppliedSidebarFilters(newFilters);

    const handleResetSidebar = () => {
        setSidebarFilters(EMPTY_FILTERS);
        setAppliedSidebarFilters({});
    };

    const handleRemoveSidebarFilter = (key) => {
        setAppliedSidebarFilters(prev => { const n = { ...prev }; delete n[key]; return n; });
        setSidebarFilters(prev => ({ ...prev, [key]: '' }));
    };

    return (
        <>
            <Header />
            <Box
                sx={{
                    minHeight: '100vh',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 0
                }}
            >
                {/* Search hero */}
                <SearchHero
                    compact
                    title={_t('Find your next job')}
                    lead={_t('Search open roles and filter by category, location and more.')}
                    onSubmit={handleQuickSearch}
                    loading={isSearching}
                    submitLabel={_t('Find jobs')}
                    fields={[
                        { key: 'q', placeholder: _t('Job title, keywords or company'), icon: SearchLucide, value: searchInput, onChange: setSearchInput },
                        { key: 'loc', placeholder: _t('City or postcode'), icon: MapPin, value: locationInput, onChange: setLocationInput }
                    ]}
                />

                {/* Main content */}
                <Container maxWidth="xl" sx={{ pt: 2, pb: 3, flex: 1 }}>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={3}>
                            <JobsFilterSidebar
                                filters={sidebarFilters}
                                onFilterChange={(name, value) =>
                                    setSidebarFilters(prev => ({ ...prev, [name]: value }))
                                }
                                onApplyFilters={handleApplySidebar}
                                onResetFilters={handleResetSidebar}
                                appliedFilters={appliedSidebarFilters}
                                onRemoveFilter={handleRemoveSidebarFilter}
                            />
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <JobsListSidebar
                                searchFilters={mergedFilters}
                                onLoadingChange={setIsSearching}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <MinimalFooter />
        </>
    );
}

export default JobsPage;
